import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { accountPath } from "@/utils/accountRoutes";
import AuroraBackground from "../../home/components/AuroraBackground";
import {
  ApplyRequestError,
  applyPackage,
  fetchPackageSetup,
  fetchPackages,
  packageSetupQueryKey,
  packagesQueryKey,
  planGuide,
  sendAssistantMessage,
  tryAgent,
} from "../packagesApi";
import { readinessQueryKey } from "../readinessApi";
import AutomationDialog from "./AutomationDialog";
import TrainingDialog from "../training/TrainingDialog";
import BuildingAgents from "./BuildingAgents";
import { planAgents } from "./GuideMode";
import { agentsToCreate } from "./agentsToCreate.js";
import { choiceField, missingChoices, withDefaultChoice } from "./skillChoices";
import { automationSetupPath, capabilityState } from "./capabilityState";
import ResultPanel from "./ResultPanel";
import StepCapabilities from "./StepCapabilities";
import StepConfigure, { ConfigureTabs } from "./StepConfigure";
import StepModels from "./StepModels";
import StepSystems from "./StepSystems";
import { SecondaryButton } from "./visuals";

/**
 * "Agentes do escritório": the setup in the order things depend on each other.
 *
 * 1. Modelo: which agent (Jurídico; the others are coming).
 * 2. Sistemas: the systems it integrates with, each shown once.
 * 3. Capacidades: what it does with them; items that need the WhatsApp wait
 *    for it instead of blocking.
 * 4. Configurar: answer a few questions, or hand over the firm's roteiro.
 *
 * The step lives in the URL (?etapa=). Connecting a system opens that
 * system's own connect dialog right here (the integrations hub, embedded), so
 * the firm never leaves the setup.
 */

const IntegrationsHub = lazy(() => import("../../implementation/IntegrationsHub"));

const STEPS = ["modelo", "sistemas", "capacidades", "configurar"];
const TITLES = {
  modelo: "Escolha o modelo do seu agente",
  sistemas: "Quais sistemas o escritório usa?",
  capacidades: "O que o agente vai fazer",
  configurar: "Como o escritório atende",
};
const BASE_SKILL = "atendimento-inicial";


/**
 * @param {{accountId?: string, onDefer?: () => void}} props
 *   As the onboarding (ImplementationEntry) the account comes as a prop and
 *   `onDefer` offers "Fazer depois".
 */
export default function AgentWizardPage({ accountId = null, onDefer = null } = {}) {
  const { clientId: paramId } = useParams();
  const clientId = paramId || accountId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: listing, isLoading: loadingModels } = useQuery({
    queryKey: packagesQueryKey(clientId),
    queryFn: () => fetchPackages(clientId),
    enabled: Boolean(clientId),
  });
  const models = listing?.models || [];
  const [modelId, setModelId] = useState(null);
  const model = models.find((m) => m.id === modelId) || models.find((m) => m.available) || null;
  const packageId = model?.packageId || null;
  const applied = listing?.packages?.find((p) => p.id === packageId)?.applied || null;

  const requested = searchParams.get("etapa");
  const step = STEPS.includes(requested) ? requested : applied ? "capacidades" : "modelo";
  const goTo = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set("etapa", next);
    setSearchParams(params);
    window.scrollTo?.({ top: 0 });
  };

  const { data: setup, isLoading: loadingSetup } = useQuery({
    queryKey: packageSetupQueryKey(clientId, packageId),
    queryFn: () => fetchPackageSetup(clientId, packageId),
    enabled: Boolean(clientId && packageId && step !== "modelo"),
  });

  // What is on: installed skills, the required base, and the rest when usable.
  const [enabled, setEnabled] = useState(null);
  const [baseValues, setBaseValues] = useState(null);
  // The other agent skills' params; only their one choice (e.g. contract models) is asked, on step 3.
  const [skillValues, setSkillValues] = useState({});
  // Cards that still miss their choice, with the reason shown on the card.
  const [attention, setAttention] = useState({});
  useEffect(() => {
    if (!setup || enabled) return;
    const next = {};
    const values = {};
    for (const s of setup.skills) {
      const { state } = capabilityState(s, { legalProviderLabel: setup.legalProvider?.label });
      next[s.id] = s.kind === "agent" && (Boolean(s.required) || Boolean(s.installed) || state === "ready" || state === "waiting");
      if (s.kind === "agent" && s.id !== BASE_SKILL) values[s.id] = withDefaultChoice(s, s.values || {});
    }
    setEnabled(next);
    setSkillValues(values);
    setBaseValues({ ...(setup.skills.find((s) => s.id === BASE_SKILL)?.values || {}) });
  }, [setup, enabled]);

  /** Sends the firm to the cards still missing their choice; true when none is. */
  const choicesMade = () => {
    const missing = missingChoices(setup.skills, enabled, skillValues);
    if (!missing.length) return true;
    setAttention(
      Object.fromEntries(
        missing.map((id) => [id, `Escolha antes de continuar: ${choiceField(setup.skills.find((s) => s.id === id)).label}`]),
      ),
    );
    goTo("capacidades");
    return false;
  };

  const baseSkill = setup?.skills.find((s) => s.id === BASE_SKILL) || null;
  const whatsappConnected = Boolean(
    setup?.systems?.flatMap((g) => g.systems).find((s) => s.id === "whatsapp" && s.status === "connected"),
  );
  const waitingCount = useMemo(() => {
    if (!setup || !enabled) return 0;
    const skills = setup.skills.filter((s) => enabled[s.id] && capabilityState(s).state === "waiting").length;
    const billing = setup.systems?.flatMap((g) => g.systems).filter((s) => s.waitingForWhatsapp).length || 0;
    return skills + billing;
  }, [setup, enabled]);

  // Connecting a system: its dialog opens here; when it closes, re-read the setup.
  const [connecting, setConnecting] = useState(null);
  const finishConnecting = useCallback(() => {
    setConnecting(null);
    queryClient.invalidateQueries({ queryKey: packageSetupQueryKey(clientId, packageId) });
    queryClient.invalidateQueries({ queryKey: readinessQueryKey(clientId) });
  }, [queryClient, clientId, packageId]);

  // Reminder or birthday dialog (step 3), by workflow id.
  const [automationWorkflow, setAutomationWorkflow] = useState(null);
  const openFullAutomationScreen = useCallback(() => {
    const path = automationSetupPath(accountPath, clientId, automationWorkflow, setup?.legalProvider?.id);
    setAutomationWorkflow(null);
    if (path) navigate(path);
  }, [automationWorkflow, clientId, setup, navigate]);

  const [trainingOpen, setTrainingOpen] = useState(false);

  // Step 4
  const [mode, setMode] = useState("questions");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [guideDraft, setGuideDraft] = useState({ text: "", file: null });
  const [guideResult, setGuideResult] = useState(null);
  const [manualEdits, setManualEdits] = useState(null);
  const [result, setResult] = useState(null);

  // The roteiro is read once per draft: "Conversar com o agente" and
  // "Montar meus agentes" share the plan (`purpose` only picks the screen).
  const plan = useMutation({ mutationFn: ({ text, file }) => planGuide(clientId, { text, file }) });
  const guidePlan = guideResult?.draft === guideDraft ? guideResult : null;
  const readRoteiro = async (purpose) => {
    if (guidePlan) return guidePlan;
    const res = await plan.mutateAsync({ text: guideDraft.text.trim(), file: guideDraft.file, purpose });
    const entry = { ...res, draft: guideDraft };
    setGuideResult(entry);
    return entry;
  };
  const guideForApply = (p) => ({ fragments: p.fragments, specialists: p.specialists, ragEnabled: p.ragEnabled });

  const apply = useMutation({
    mutationFn: ({ guide, overwrite }) => {
      const skills = {};
      for (const s of setup.skills) {
        if (s.kind !== "agent" || !enabled?.[s.id]) continue;
        const { state } = capabilityState(s);
        if (state !== "ready" && state !== "waiting") continue;
        skills[s.id] = s.id === BASE_SKILL ? { ...baseValues, ...(guide?.params || {}) } : skillValues[s.id] || s.values || {};
      }
      return applyPackage(clientId, packageId, {
        skills,
        overwriteManualEdits: overwrite,
        guide: guide ? guideForApply(guide) : null,
      });
    },
    onSuccess: (res, vars) => {
      setManualEdits(null);
      setResult({ ...res, fromGuide: Boolean(vars.guide) });
      queryClient.invalidateQueries({ queryKey: packagesQueryKey(clientId) });
      queryClient.invalidateQueries({ queryKey: readinessQueryKey(clientId) });
    },
    onError: (err, vars) => {
      if (err instanceof ApplyRequestError && err.code === "MANUAL_EDITS") {
        setManualEdits({ nodes: err.details?.nodes || [], guide: vars.guide });
        return;
      }
      if (err instanceof ApplyRequestError && err.code === "NOT_READY") {
        // Something the setup did not know is missing: show it on each card, with its reason.
        const blocked = err.details?.blocked || {};
        setAttention(
          Object.fromEntries(Object.entries(blocked).map(([id, checks]) => [id, checks?.[0]?.title || "Falta configurar este item."])),
        );
        queryClient.invalidateQueries({ queryKey: packageSetupQueryKey(clientId, packageId) });
        goTo("capacidades");
        return;
      }
      if (err instanceof ApplyRequestError && err.code === "INVALID_PARAMS") {
        const errors = err.details?.errors || {};
        // A capability's own choice is made on its card: go there, with the reason on it.
        const onCards = Object.entries(errors).filter(([id]) => id !== BASE_SKILL);
        if (onCards.length) {
          setAttention(Object.fromEntries(onCards.map(([id, fields]) => [id, Object.values(fields)[0] || err.message])));
          goTo("capacidades");
          return;
        }
        toast.error(Object.values(errors[BASE_SKILL] || {})[0] || err.message);
        return;
      }
      toast.error(err.message || "Não foi possível criar os agentes agora.");
    },
  });

  const ask = async (message) => {
    const turn = await sendAssistantMessage(clientId, packageId, message);
    const fromDraft = turn?.draft?.values?.[BASE_SKILL];
    if (fromDraft) setBaseValues((prev) => ({ ...prev, ...fromDraft }));
    const said = [...(turn?.entries || [])].reverse().find((e) => e.role === "assistant" && e.text)?.text;
    return said || "Anotado.";
  };

  // "Conversar com o agente", on the phone of step 4.
  const guideReady = Boolean(guideDraft.file) || guideDraft.text.trim().length > 0;
  const tryAgentProps = {
    agents: agentsToCreate(setup?.skills, enabled, mode === "guide" ? guidePlan?.plan : null),
    agentsNote:
      mode === "guide" && !guidePlan ? "Com o roteiro, a triagem pode virar um agente por área que ele descreve." : null,
    canTry: mode === "questions" || guideReady,
    hint: mode === "guide" ? "Envie ou escreva o roteiro para conversar com o agente." : null,
    typingLabel: mode === "guide" && !guidePlan ? "lendo o seu roteiro..." : "digitando...",
    onReply: async (messages) => {
      let params = baseValues;
      let guide = null;
      if (mode === "guide") {
        const read = await readRoteiro("try");
        params = { ...baseValues, ...(read.plan.params || {}) };
        guide = guideForApply(read.plan);
      }
      return tryAgent(clientId, packageId, { params, guide, messages });
    },
  };

  const stepNumber = STEPS.indexOf(step) + 1;
  const buildingPhase = plan.isPending && plan.variables?.purpose === "build" ? "reading" : apply.isPending ? "building" : null;
  const connectDialog = connecting && (
    <Suspense fallback={null}>
      <IntegrationsHub connectId={connecting} onFinished={finishConnecting} />
    </Suspense>
  );
  const loading = loadingModels || (step !== "modelo" && (loadingSetup || !enabled || !baseValues));

  if (result) {
    return (
      <Backdrop className="py-16">
        <ResultPanel
          agentCount={result.agentCount || 1}
          agents={result.agents?.length ? result.agents : result.fromGuide ? planAgents(guideResult?.plan) : []}
          document={result.fromGuide ? guideResult?.document : null}
          whatsappConnected={whatsappConnected}
          waitingCount={waitingCount}
          onOpenBuilder={() => navigate(accountPath(clientId, `flow/${result.flowId}`))}
          onConnectWhatsapp={() => setConnecting("whatsapp")}
          secondaryLabel={result.fromGuide ? "Ajustar o roteiro" : "Ajustar as respostas"}
          onSecondary={() => setResult(null)}
          onTraining={() => setTrainingOpen(true)}
        />
        {connectDialog}
        {trainingOpen && <TrainingDialog accountId={clientId} onClose={() => setTrainingOpen(false)} />}
      </Backdrop>
    );
  }

  return (
    <Backdrop className="pb-16 pt-12 sm:pt-16">
      <main className="relative mx-auto w-full max-w-[1080px]">
        <div className="flex items-center justify-between gap-4">
          <p className="text-[15px] text-muted-foreground">
            Passo {stepNumber} de {STEPS.length}
            {step !== "modelo" && model ? ` · ${model.label}` : ""}
          </p>
          {onDefer && (
            <button type="button" onClick={onDefer} className="min-h-[40px] text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
              Fazer depois
            </button>
          )}
        </div>
        <div className="mb-8 mt-1.5 flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-[28px] font-semibold tracking-tight sm:text-[34px]">{TITLES[step]}</h1>
          {step === "configurar" && !buildingPhase && <ConfigureTabs mode={mode} onMode={setMode} />}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[180px] rounded-2xl" />
            ))}
          </div>
        ) : step === "modelo" ? (
          <StepModels
            models={models}
            selected={model?.id || null}
            onSelect={setModelId}
            onNext={() => goTo("sistemas")}
            onBlank={() => navigate(accountPath(clientId, "automation"))}
          />
        ) : step === "sistemas" ? (
          <StepSystems
            groups={setup.systems || []}
            waitingCount={waitingCount}
            onConnect={setConnecting}
            onBack={() => goTo("modelo")}
            onNext={() => goTo("capacidades")}
          />
        ) : step === "capacidades" ? (
          <StepCapabilities
            skills={setup.skills}
            enabled={enabled}
            legalProviderLabel={setup.legalProvider?.label}
            values={skillValues}
            attention={attention}
            onToggle={(id, on) => {
              setEnabled((prev) => ({ ...prev, [id]: on }));
              if (!on) setAttention(({ [id]: _, ...rest }) => rest);
            }}
            onChoice={(id, name, value) => {
              setSkillValues((prev) => ({ ...prev, [id]: { ...prev[id], [name]: value } }));
              setAttention(({ [id]: _, ...rest }) => rest);
            }}
            onConnectSystems={() => goTo("sistemas")}
            onConfigureAutomation={(s) => setAutomationWorkflow(s.workflow)}
            onBack={() => goTo("sistemas")}
            onNext={() => choicesMade() && goTo("configurar")}
          />
        ) : buildingPhase ? (
          <BuildingAgents phase={buildingPhase} fromGuide={buildingPhase === "reading" || Boolean(apply.variables?.guide)} />
        ) : (
          <>
            {manualEdits && (
              <div role="alert" className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200/70 bg-amber-50 px-4 py-3 dark:border-amber-400/20 dark:bg-amber-500/10">
                <p className="min-w-0 flex-1 text-sm text-amber-900 dark:text-amber-100">
                  Alguns agentes foram editados no Touch Builder. Criar de novo substitui essas edições.
                </p>
                <SecondaryButton onClick={() => setManualEdits(null)}>Cancelar</SecondaryButton>
                <button
                  type="button"
                  onClick={() => apply.mutate({ guide: manualEdits.guide, overwrite: true })}
                  className="inline-flex min-h-[44px] items-center rounded-full bg-foreground px-5 text-sm font-semibold text-background"
                >
                  Substituir e criar
                </button>
              </div>
            )}
            <StepConfigure
              accountId={clientId}
              firmName={setup.firmName}
              mode={mode}
              baseSkill={baseSkill}
              baseValues={baseValues}
              onBaseValues={setBaseValues}
              onApply={(guide) => choicesMade() && apply.mutate({ guide, overwrite: false })}
              questionIndex={questionIndex}
              onQuestionIndex={setQuestionIndex}
              applying={apply.isPending}
              onAsk={setup.assistant?.available ? ask : null}
              tryAgent={tryAgentProps}
              guide={{
                draft: guideDraft,
                onDraft: (next) => {
                  plan.reset();
                  setGuideDraft(next);
                },
                onBuild: async () => {
                  if (!choicesMade()) return;
                  try {
                    const read = await readRoteiro("build");
                    apply.mutate({ guide: read.plan, overwrite: false });
                  } catch {
                    // The error shows on the roteiro (plan.error).
                  }
                },
                error:
                  plan.error && plan.variables?.purpose === "build"
                    ? plan.error.message || "Não conseguimos ler o roteiro agora. Tente de novo."
                    : null,
              }}
            />
          </>
        )}
      </main>
      {connectDialog}
      {automationWorkflow && (
        <AutomationDialog
          accountId={clientId}
          workflow={automationWorkflow}
          firmName={setup?.firmName}
          onClose={() => setAutomationWorkflow(null)}
          onUnsupported={openFullAutomationScreen}
          onCreated={() => queryClient.invalidateQueries({ queryKey: packageSetupQueryKey(clientId, packageId) })}
        />
      )}
    </Backdrop>
  );
}

/** The home's aurora behind the setup, so it reads as the same product. */
function Backdrop({ className, children }) {
  return (
    <div className={`relative min-h-full overflow-hidden bg-[#f7f8fc] px-4 text-foreground dark:bg-[#0a0a0f] sm:px-8 ${className}`}>
      <AuroraBackground scenario="ok" />
      {children}
    </div>
  );
}
